import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  base64Encode,
  explore,
  Filter,
  FilterService,
  isDefinedNotEmpty,
  Sort,
  SortService,
  Store,
  withFilter,
  withSearch,
  withSort,
} from '@softline/core';
import {
  SOFTLINE_CONFIG_WWS_WARENUEBERNAHME_BESTELLUNG_STEPS,
  SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BESTELLUNG,
  SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_LIEFERANT,
} from '../../../warenuebernahme.shared';
import {
  BackNavigable,
  BackNavigationService,
  FilterAndSortComponent,
  FilterConfig,
  HeaderComponent,
  SortConfig,
  StepHeaderComponent,
} from '@softline/application';
import { Router, RouterModule } from '@angular/router';
import { WwsLieferantStore } from '../../../store/lieferant.store';
import { combineLatestWith, map, Observable } from 'rxjs';
import { LieferantBestellung } from '../../../types/bestellung';
import { toSignal } from '@angular/core/rxjs-interop';
import { PageComponent, PageHeaderComponent, Panel, PanelHeaderComponent } from '@softapps/core/pages';
import {
  inputFn,
  RepeatDirective,
  ScrollIntoViewDirective,
  TranslatePipe,
} from '@softline/ui-core';
import { WwsBestellungStore } from '../../../store/bestellung.store';

@Component({
  selector: 'soft-bestellung-schritt-1',
  imports: [
    CommonModule,
    HeaderComponent,
    StepHeaderComponent,
    RouterModule,
    FilterAndSortComponent,
    PageComponent,
    PageHeaderComponent,
    Panel,
    PanelHeaderComponent,
    ScrollIntoViewDirective,
    RepeatDirective,
    TranslatePipe,
  ],
  templateUrl: './bestellung-schritt-1.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./bestellung-schritt-1.component.css'],
})
export class BestellungSchritt1Component implements OnInit, OnDestroy, BackNavigable {
  readonly steps = SOFTLINE_CONFIG_WWS_WARENUEBERNAHME_BESTELLUNG_STEPS;

  readonly lieferanten$ = this.store.observe(
    SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_LIEFERANT,
    WwsLieferantStore.getters.all
  );

  readonly lieferanten = toSignal(this.lieferanten$);

  readonly explorerLieferanten = explore(
    this.lieferanten,
    withFilter(),
    withSort(),
    withSearch()
  );

  readonly sort$ = this.store.observe(
    SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_LIEFERANT,
    WwsLieferantStore.getters.sort
  );

  readonly filter$ = this.store.observe(
    SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_LIEFERANT,
    WwsLieferantStore.getters.filter
  );

  readonly input = inputFn();

  readonly filteredItems$: Observable<LieferantBestellung[]> = this.lieferanten$.pipe(
    combineLatestWith(this.sort$, this.filter$),
    map(([values, sort, filter]) =>
      this.sortService.sort(this.filterService.filter(values, filter), sort)
    )
  );

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_LIEFERANT,
    WwsLieferantStore.getters.loading
  );

  filterConfig: FilterConfig = {
    properties: [
      {
        property: 'lieferant.lieferantennummer',
        title: '#WWS_WARENUEBERNAHME.FILTER_SORT.TITEL_LIEFERANT_NUMMER',
      },
      {
        property: 'lieferant.kurzbez',
        title: '#WWS_WARENUEBERNAHME.FILTER_SORT.TITEL_LIEFERANT_BEZEICHNUNG',
      },
      {
        property: 'lieferant.id',
        title: '#WWS_WARENUEBERNAHME.FILTER_SORT.TITEL_LIEFERANT_ID',
      },
    ],
  };

  sortConfig: SortConfig = {
    properties: [
      {
        property: 'lieferant.lieferantennummer',
        title: '#WWS_WARENUEBERNAHME.FILTER_SORT.TITEL_LIEFERANT_NUMMER',
      },
      {
        property: 'lieferant.kurzbez',
        title: '#WWS_WARENUEBERNAHME.FILTER_SORT.TITEL_LIEFERANT_BEZEICHNUNG',
      },
      {
        property: 'lieferant.id',
        title: '#WWS_WARENUEBERNAHME.FILTER_SORT.TITEL_LIEFERANT_ID',
      },
    ],
  };

  constructor(
    private store: Store,
    private router: Router,
    private sortService: SortService,
    private filterService: FilterService,
    private backNavigationService: BackNavigationService
  ) {}

  async ngOnInit(): Promise<void> {
    this.backNavigationService.set(this);

    this.store.commit(
      SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_LIEFERANT,
      WwsLieferantStore.mutations.setSort,
      {
        property: 'lieferant.kurzbez',
        title: '#WWS_WARENUEBERNAHME.FILTER_SORT.TITEL_LIEFERANT_BEZEICHNUNG',
        direction: 'asc',
      } as any
    );
  }
  ngOnDestroy(): void {
    this.store.commit(
      SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_LIEFERANT,
      WwsLieferantStore.mutations.setFilter,
      null
    );

    this.store.commit(
      SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_LIEFERANT,
      WwsLieferantStore.mutations.setSort,
      null
    );

    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/warenuebernahme']);
  }

  async enterOrderNumber(): Promise<void> {
    const result = await this.input({
      title: 'Bestellnummer',
      placeholder: 'Bestellnummer eingeben...',
      saveButtonText: 'Bestellung übernehmen',
      subtitle: 'Geben Sie die Bestellnummer ein',
      textValue: '',
      multiline: false,
    });

    if (!isDefinedNotEmpty(result) || result === 'DISMISSED') return;

    const orders = await this.store.dispatch(
      SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BESTELLUNG,
      WwsBestellungStore.actions.loadMany,
      {
        clear: true,
        queryParams: {
          bestellnummern: result,
        },
      }
    );

    if (orders.length > 0) {
      const entities = this.store.get(
        SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_BESTELLUNG,
        WwsBestellungStore.getters.entities
      );

      const ids = orders.map((order) => order.id);

      const data = {
        ids,
        bestellnummern: ids.map((id) => entities[id]?.bestellnummer ?? ''),
      };

      await this.router.navigate([
        '/warenuebernahme/bestellung/detail',
        base64Encode(JSON.stringify(data)),
      ]);
    }
  }

  /*
  async onFilterChange(filter: Filter | null): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_LIEFERANT,
      WwsLieferantStore.mutations.setFilter,
      filter
    );
  }

  async onSortChange(sort: Sort | null): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_WWS_WARENUEBERNAHME_LIEFERANT,
      WwsLieferantStore.mutations.setSort,
      sort
    );
  }*/
}
